import isFinite from 'lodash/isFinite';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import styled from 'styled-components';
import {
  closeInnerPopup,
  closeTopLevelPopup,
  openInnerPopup,
  openTopLevelPopup,
} from '../../../../data/providers/popupsProvider';
import {
  EZScorePreference,
  IAggregateWithMetric,
  IMetric,
  ISelectedLabelKey,
  Level,
  MetricDirection,
} from '../../../../data/types';
import { roundValue } from '../../../../utils/math';
import { Column } from '../../../layout/Column';
import { Row } from '../../../layout/Row';
import { Icon } from '../../../shared/Icon';
import { ListItem, ListItemCell } from '../../../shared/List/ListItem';
import { Metric } from '../../../shared/Metric';
import { ScoringDescription } from './ScoringDescription';

interface IScoreProps extends RouteComponentProps {
  metric: IMetric;
  style: React.CSSProperties;
  aggregate: IAggregateWithMetric;
  loading: boolean;
  onMetricClick: (s: ISelectedLabelKey) => void;
  listShape: number[];
  scale: EZScorePreference;
  level?: Level;
}
export const Score = withRouter(
  ({
    metric,
    style,
    aggregate,
    loading = true,
    onMetricClick,
    listShape,
    scale,
    level,
    history,
  }: IScoreProps): React.ReactElement => {
    const handleRowClick = (): void =>
      onMetricClick({ key: metric.key, label: metric.label });

    const openScoringDescription = (
      e: React.MouseEvent<HTMLAnchorElement>
    ): void => {
      e.preventDefault();
      e.stopPropagation();
      if (level === 2) {
        openTopLevelPopup(
          <ScoringDescription onClose={closeTopLevelPopup} level={level} />
        );
      } else {
        openInnerPopup(
          <ScoringDescription
            onClose={closeInnerPopup(history)}
            level={level}
          />,
          history,
          'scoring-description'
        );
      }
    };

    const value = isFinite(metric.value)
      ? roundValue(metric.value, metric.roundingPrecision)
      : null;

    return (
      <ListItem style={style} onClick={handleRowClick}>
        <ListItemCell style={{ flex: listShape[0] }}>
          <Column>
            <Metric
              label={metric?.label}
              value={value}
              units={metric.units}
              isBoolean={metric.isBoolean}
            />
          </Column>
        </ListItemCell>
        <ListItemCell style={{ flex: listShape[1] }}>
          {!loading ? (
            <Metric
              value={roundValue(aggregate?.mean, metric.roundingPrecision)}
              units={metric.units}
              isSecondary
            />
          ) : null}
        </ListItemCell>
        <ListItemCell style={{ flex: listShape[2] }}>
          {!loading ? (
            <Row>
              <Metric
                value={roundValue(aggregate?.change, 1)}
                units='%'
                isSecondary
              />
            </Row>
          ) : null}
        </ListItemCell>
        <ListItemCell style={{ flex: listShape[3] }}>
          {!loading ? (
            <Metric
              value={
                isNaN(aggregate?.zscore) || aggregate?.count < 3
                  ? '--'
                  : aggregate?.zscore
              }
              isSecondary
            />
          ) : null}
        </ListItemCell>
        <ListItemCell>
          <TrafficLight
            direction={metric.direction}
            zscore={aggregate?.zscore}
            scale={scale}
            count={aggregate?.count}
            value={value}
            mean={aggregate?.mean}
            onDescription={openScoringDescription}
          />
        </ListItemCell>
      </ListItem>
    );
  }
);

const interpreteZScore = (
  zscore: number,
  scale: EZScorePreference,
  direction: MetricDirection,
  value: number,
  mean: number
): { color: string; direction: 'north' | 'south' | 'neutral' } => {
  if (direction !== 0) {
    if (scale === EZScorePreference.Scaled) {
      if (zscore <= 25) {
        return {
          color: 'var(--color-material-500-red)',
          direction: 'south',
        };
      }
      if (zscore <= 37.5) {
        return {
          color: 'var(--color-material-500-yellow)',
          direction: 'south',
        };
      }
      if (zscore >= 75) {
        return {
          color: 'var(--color-material-500-green)',
          direction: 'north',
        };
      }
      if (zscore >= 62.5) {
        return {
          color: 'var(--color-material-500-yellow)',
          direction: 'north',
        };
      }
      return {
        color: 'transparent',
        direction: 'neutral',
      };
    } else {
      if (zscore <= -2) {
        return {
          color: 'var(--color-material-500-red)',
          direction: 'south',
        };
      }
      if (zscore <= -1) {
        return {
          color: 'var(--color-material-500-yellow)',
          direction: 'south',
        };
      }
      if (zscore >= 2) {
        return {
          color: 'var(--color-material-500-green)',
          direction: 'north',
        };
      }
      if (zscore >= 1) {
        return {
          color: 'var(--color-material-500-yellow)',
          direction: 'north',
        };
      }
      return {
        color: 'transparent',
        direction: 'neutral',
      };
    }
  } else {
    const better = Math.abs(value) < Math.abs(mean);
    if (scale === EZScorePreference.Scaled) {
      if (zscore <= 25) {
        return {
          color: 'var(--color-material-500-red)',
          direction: better ? 'north' : 'south',
        };
      }
      if (zscore <= 37.5) {
        return {
          color: 'var(--color-material-500-yellow)',
          direction: better ? 'north' : 'south',
        };
      }
      if (zscore >= 75) {
        return {
          color: 'var(--color-material-500-red)',
          direction: better ? 'north' : 'south',
        };
      }
      if (zscore >= 62.5) {
        return {
          color: 'var(--color-material-500-yellow)',
          direction: better ? 'north' : 'south',
        };
      }
      return {
        color: 'transparent',
        direction: 'neutral',
      };
    } else {
      if (zscore <= -2) {
        return {
          color: 'var(--color-material-500-red)',
          direction: better ? 'north' : 'south',
        };
      }
      if (zscore <= -1) {
        return {
          color: 'var(--color-material-500-yellow)',
          direction: better ? 'north' : 'south',
        };
      }
      if (zscore >= 2) {
        return {
          color: 'var(--color-material-500-red)',
          direction: better ? 'north' : 'south',
        };
      }
      if (zscore >= 1) {
        return {
          color: 'var(--color-material-500-yellow)',
          direction: better ? 'north' : 'south',
        };
      }
      return {
        color: 'transparent',
        direction: 'neutral',
      };
    }
  }
};

const TrafficLight = (props: {
  zscore: number;
  scale: EZScorePreference;
  count: number;
  direction: MetricDirection;
  value: number;
  mean: number;
  onDescription: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}): React.ReactElement => {
  const { color, direction } = interpreteZScore(
    props.zscore,
    props.scale,
    props.direction,
    props.value,
    props.mean
  );

  return (
    <Light href='#' onClick={props.onDescription}>
      {props.count > 2 && (
        <IconContainer bgColor={color}>
          {direction !== 'neutral' ? (
            direction === 'north' ? (
              <Icon name='north' style={{ color: 'var(--color-dark-text)' }} />
            ) : (
              <Icon name='south' style={{ color: 'var(--color-dark-text)' }} />
            )
          ) : null}
        </IconContainer>
      )}
    </Light>
  );
};

const IconContainer = styled.div<{ bgColor: string }>`
  height: 25px;
  width: 25px;
  color: var(--color-dark-text);
  border-radius: 50%;
  background-color: ${(props: { bgColor: string }): string => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Light = styled.a`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;
